import { useCallback, useEffect, useRef } from "react";

import type { MosaicJob, MosaicJobResult } from "../editor/types";

/** Everything about a render except the pixels and the request id. */
export type EffectLayers = Omit<MosaicJob, "requestId" | "imageData">;

interface PendingRequest {
  resolve: (imageData: ImageData) => void;
  reject: (error: Error) => void;
}

interface QueuedRequest {
  requestId: number;
  imageData: ImageData;
  layers: EffectLayers;
  pending: PendingRequest[];
}

interface InFlightRequest {
  requestId: number;
  pending: PendingRequest[];
}

export function useMosaicProcessor() {
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const queuedRef = useRef<QueuedRequest | null>(null);
  const inFlightRef = useRef<InFlightRequest | null>(null);
  const startNextRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const worker = new Worker(
      new URL("../workers/mosaicWorker.ts", import.meta.url),
      { type: "module" },
    );
    workerRef.current = worker;

    const rejectOutstanding = (error: Error) => {
      inFlightRef.current?.pending.forEach(({ reject }) => reject(error));
      queuedRef.current?.pending.forEach(({ reject }) => reject(error));
      inFlightRef.current = null;
      queuedRef.current = null;
    };

    const startNext = () => {
      if (
        workerRef.current !== worker ||
        inFlightRef.current ||
        !queuedRef.current
      ) {
        return;
      }

      const next = queuedRef.current;
      queuedRef.current = null;
      inFlightRef.current = {
        requestId: next.requestId,
        pending: next.pending,
      };

      const copiedData = new Uint8ClampedArray(next.imageData.data);
      const transferableImage = new ImageData(
        copiedData,
        next.imageData.width,
        next.imageData.height,
      );
      const job: MosaicJob = {
        requestId: next.requestId,
        imageData: transferableImage,
        ...next.layers,
      };

      try {
        worker.postMessage(job, [copiedData.buffer]);
      } catch (error) {
        inFlightRef.current = null;
        const failure =
          error instanceof Error
            ? error
            : new Error("モザイク処理を開始できませんでした");
        next.pending.forEach(({ reject }) => reject(failure));
        startNext();
      }
    };
    startNextRef.current = startNext;

    worker.onmessage = (event: MessageEvent<MosaicJobResult>) => {
      const inFlight = inFlightRef.current;
      if (!inFlight || inFlight.requestId !== event.data.requestId) return;
      inFlightRef.current = null;
      if (event.data.error || !event.data.imageData) {
        const error = new Error(
          event.data.error ?? "モザイク処理結果を取得できませんでした",
        );
        inFlight.pending.forEach(({ reject }) => reject(error));
      } else {
        const output = event.data.imageData;
        inFlight.pending.forEach(({ resolve }) => resolve(output));
      }
      startNext();
    };
    worker.onerror = () => {
      if (workerRef.current !== worker) return;
      rejectOutstanding(new Error("モザイク処理ワーカーが停止しました"));
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
      workerRef.current = null;
    };
    return () => {
      worker.onmessage = null;
      worker.onerror = null;
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
      startNextRef.current = () => undefined;
      rejectOutstanding(
        new Error("モザイク処理がキャンセルされました"),
      );
    };
  }, []);

  return useCallback(
    (imageData: ImageData, layers: EffectLayers): Promise<ImageData> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(
          new Error("モザイク処理ワーカーを初期化できませんでした"),
        );
      }
      const requestId = ++requestIdRef.current;
      return new Promise((resolve, reject) => {
        const pending = { resolve, reject };
        const supersededPending = queuedRef.current?.pending ?? [];
        queuedRef.current = {
          requestId,
          imageData,
          layers,
          // A superseded queued caller observes the latest result. This keeps
          // every Promise settled without surfacing expected slider churn as
          // an application error.
          pending: [...supersededPending, pending],
        };
        startNextRef.current();
      });
    },
    [],
  );
}
